import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import {
    PerformanceWidgetFreqs,
    PerformanceWidgetInfo,
    PerformanceWidgetPerfs,
} from "@tecumseh/Services/PerformanceWidgetService";
import { Draft } from "immer";

type PerformanceWidgetState = Readonly<{
    isLoadingWidgetInfo: boolean;
    isLoadingWidgetFreq: boolean;
    isLoadingWidgetPerf: boolean;
    isAllApiLoading: boolean;
    widgetInfo?: PerformanceWidgetInfo;
    widgetFreqs?: PerformanceWidgetFreqs;
    widgetPerfs?: PerformanceWidgetPerfs;
    performanceFilters: {
        selectedFreq?: string;
        selectedFluid?: string;
    };
    widgetPerfsData: Record<string, any>;
}>;

const initialState: PerformanceWidgetState = {
    isLoadingWidgetFreq: false,
    isLoadingWidgetInfo: false,
    isLoadingWidgetPerf: false,
    isAllApiLoading: false,
    performanceFilters: {},
    widgetPerfsData: {},
};

const customPerformanceWidgetInfo = createTypedReducerWithImmer(initialState, {
    "Custom/WidgetInfo/BeginLoadWidgetInfo": (draft: Draft<PerformanceWidgetState>) => {
        draft.isLoadingWidgetInfo = true;
        draft.isAllApiLoading = true;
    },
    "Custom/WidgetInfo/CompleteLoadWidgetInfo": (
        draft: Draft<PerformanceWidgetState>,
        action: { widgetInfo: PerformanceWidgetInfo },
    ) => {
        draft.isLoadingWidgetInfo = false;
        draft.widgetInfo = action.widgetInfo;
    },
    "Custom/WidgetInfo/BeginLoadWidgetFreqs": (draft: Draft<PerformanceWidgetState>) => {
        draft.isLoadingWidgetFreq = true;
    },
    "Custom/WidgetInfo/CompleteLoadWidgetFreqs": (
        draft: Draft<PerformanceWidgetState>,
        action: { widgetFreqs: PerformanceWidgetFreqs },
    ) => {
        draft.isLoadingWidgetFreq = false;
        draft.widgetFreqs = action.widgetFreqs;
    },
    "Custom/WidgetInfo/BeginLoadWidgetPerfs": (draft: Draft<PerformanceWidgetState>) => {
        draft.isLoadingWidgetPerf = true;
    },
    "Custom/WidgetInfo/CompleteLoadWidgetPerfs": (
        draft: Draft<PerformanceWidgetState>,
        action: { widgetPerfs: PerformanceWidgetPerfs },
    ) => {
        draft.isLoadingWidgetPerf = false;
        draft.widgetPerfs = action.widgetPerfs;
        draft.isAllApiLoading = false;
    },
    "Custom/WidgetInfo/SetWidgetPerfsData": (
        draft: Draft<PerformanceWidgetState>,
        action: { combinationKey: string; widgetPerfsData: any },
    ) => {
        if (!draft.widgetPerfsData) {
            draft.widgetPerfsData = {};
        }

        draft.widgetPerfsData[action.combinationKey] = action.widgetPerfsData;
    },
    "Custom/WidgetInfo/SetSelectedFluid": (draft: Draft<PerformanceWidgetState>, action: { selectedFluid: string }) => {
        draft.performanceFilters.selectedFluid = action.selectedFluid;
    },
    "Custom/WidgetInfo/SetSelectedFreq": (draft: Draft<PerformanceWidgetState>, action: { selectedFreq: string }) => {
        draft.performanceFilters.selectedFreq = action.selectedFreq;
    },

    "Custom/WidgetInfo/Reset": (draft: Draft<PerformanceWidgetState>) => {
        draft.isLoadingWidgetFreq = initialState.isLoadingWidgetFreq;
        draft.isLoadingWidgetInfo = initialState.isLoadingWidgetInfo;
        draft.isLoadingWidgetPerf = initialState.isLoadingWidgetPerf;
        draft.isAllApiLoading = initialState.isAllApiLoading;
        draft.widgetInfo = initialState.widgetInfo;
        draft.widgetFreqs = initialState.widgetFreqs;
        draft.widgetPerfs = initialState.widgetPerfs;
        draft.performanceFilters = initialState.performanceFilters;
    },
});

(reducers as any).customPerformanceWidgetInfo = customPerformanceWidgetInfo;

export type PerformanceWidgetInfoState = {
    customPerformanceWidgetInfo: PerformanceWidgetState;
} & ApplicationState;

type Reducers = {
    customPerformanceWidgetInfo: typeof customPerformanceWidgetInfo;
};

export type CustomActions = Parameters<Reducers[keyof Reducers]>[1] | AnyAction;
